/* Le configurazioni di un gruppo: schede sotto i 720px, tabella sopra.
 *
 * Le due forme si renderizzano ENTRAMBE e sceglie il CSS (`.sb-lista` e
 * `.sb-tabella-guscio` si escludono a 720px). Così il primo fotogramma è già
 * giusto e ruotare il telefono non rimonta l'albero.
 *
 * La scelta delle colonne vale su tutte e due. Una selezione che agisse solo
 * sulla tabella servirebbe tutte le larghezze tranne quella in cui il problema
 * esiste davvero.
 *
 * Le intestazioni NON sono cliccabili: l'ordinamento è una query e sta nel suo
 * controllo dedicato, sopra la lista.
 */

import {
  ContenitoreDati,
  IconaStrategia,
  Intorno,
  NeiRibassi,
  NumeroConSoglia,
  TernaMetriche,
  Tenuta,
  VeloInterrogazione,
  divisioneDaMetodo,
} from '../../componenti';
import { drawdown as fmtDrawdown, num, pct } from '../../lib/formato';
import {
  leggiAPariRischio,
  leggiIntorno,
  leggiNeiRibassi,
  leggiTenuta,
  tonoRendimento,
  type LetturaSoglia,
} from '../../lib/metriche';
import type { BacktestRiga } from '../../api/tipi';
import type { StatoDati } from '../../api/usaRichiesta';
import { parametriLeggibili, periodoDiRiga } from './dati';
import type { SceltaColonne } from './colonne';

/* Il rango non è una proprietà della riga ma della QUERY: dipende
   dall'ordinamento chiesto al server e dalla pagina. Si calcola qui, dove
   quelle due cose sono note, e non si salva da nessuna parte. */
function rango(indice: number, prima: number): number {
  return prima + indice + 1;
}

interface Letture {
  tenuta: LetturaSoglia;
  ribassi: LetturaSoglia;
  intorno: LetturaSoglia;
  pariRischio: LetturaSoglia;
}

function letture(r: BacktestRiga): Letture {
  return {
    tenuta: leggiTenuta(r.walkForwardScore, divisioneDaMetodo(r.walkForwardMetodo)),
    ribassi: leggiNeiRibassi(r.bearYearsAvg, r.bearYearsCount),
    intorno: leggiIntorno(r.intorno, r.intornoParametro, r.intornoAssi),
    pariRischio: leggiAPariRischio(r.strategyChange, r.drawdownMaxValue),
  };
}

export function TabellaConfigurazioni({
  righe,
  scelta,
  stato,
  causaVuoto,
  messaggioErrore,
  onRiprova,
  onApri,
  primaPosizione,
  interrogazioneInCorso,
}: {
  righe: BacktestRiga[];
  scelta: SceltaColonne;
  stato: StatoDati;
  causaVuoto: string;
  messaggioErrore?: string;
  onRiprova?: () => void;
  onApri: (id: number) => void;
  /** Quante righe vengono prima di queste nell'ordinamento corrente. */
  primaPosizione: number;
  interrogazioneInCorso?: string | null;
}) {
  return (
    <div className="sb-relativo">
      {interrogazioneInCorso && <VeloInterrogazione testo={interrogazioneInCorso} />}

      <ContenitoreDati
        stato={stato}
        causaVuoto={causaVuoto}
        messaggioErrore={messaggioErrore}
        onRiprova={onRiprova}
      >
        {/* ---------- telefono ---------- */}
        <div className="sb-lista">
          {righe.map((r, i) => (
            <SchedaConfigurazione
              key={r.id}
              riga={r}
              posizione={rango(i, primaPosizione)}
              scelta={scelta}
              onApri={onApri}
            />
          ))}
        </div>

        {/* ---------- tablet e desktop ---------- */}
        <div className="sb-tabella-guscio">
          <div className="sb-scorri-x">
            <table className="sb-tabella">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Parametri</th>
                  <th className="sb-col-profitto">Profitto</th>
                  <th className="sb-col-drawdown">Drawdown</th>
                  <th>Ops</th>
                  {scelta.tenuta && <th>Tenuta</th>}
                  {scelta.ribassi && <th>Nei ribassi</th>}
                  {scelta.intorno && <th>Intorno</th>}
                  {scelta.pariRischio && <th>A pari rischio</th>}
                  {scelta.calmar && <th>Calmar</th>}
                  {scelta.sharpe && <th>Sharpe</th>}
                  {scelta.cagr && <th>CAGR</th>}
                  <th>Periodo</th>
                </tr>
              </thead>
              <tbody>
                {righe.map((r, i) => {
                  const l = letture(r);
                  const posizione = rango(i, primaPosizione);
                  return (
                    <tr key={r.id} style={{ cursor: 'pointer' }} onClick={() => onApri(r.id)}>
                      <td
                        className={`sb-gr-rango${posizione <= 3 ? ' sb-gr-rango--testa' : ''}`}
                        style={{ textAlign: 'left' }}
                      >
                        #{num(posizione)}
                      </td>
                      <td className="sb-cella-testo" style={{ textAlign: 'left' }}>
                        <span className="sb-tabella__strategia">
                          <IconaStrategia nome={r.strategyName} dim={24} />
                          <span className="sb-gr-parametri">
                            {parametriLeggibili(r.strategyParams)}
                          </span>
                        </span>
                      </td>
                      <td className={`sb-tono-${tonoRendimento(r.strategyChange)}`}>
                        {r.strategyChange == null ? 'n/d' : pct(r.strategyChange)}
                      </td>
                      <td className="sb-tono-perdita">
                        {r.drawdownMaxValue == null ? 'n/d' : fmtDrawdown(r.drawdownMaxValue)}
                      </td>
                      <td className="sb-tono-neutro">{num(r.tradesNumber)}</td>
                      {scelta.tenuta && (
                        <td>
                          <NumeroConSoglia densita="cella" lettura={l.tenuta} />
                        </td>
                      )}
                      {scelta.ribassi && (
                        <td>
                          <NumeroConSoglia densita="cella" lettura={l.ribassi} />
                        </td>
                      )}
                      {scelta.intorno && (
                        <td>
                          <NumeroConSoglia densita="cella" lettura={l.intorno} />
                        </td>
                      )}
                      {scelta.pariRischio && (
                        <td>
                          <NumeroConSoglia densita="cella" lettura={l.pariRischio} />
                        </td>
                      )}
                      {scelta.calmar && (
                        <td className="sb-tono-neutro">
                          {r.calmar == null ? 'n/d' : num(r.calmar, 2)}
                        </td>
                      )}
                      {scelta.sharpe && (
                        <td className="sb-tono-neutro">
                          {r.sharpe == null ? 'n/d' : num(r.sharpe, 2)}
                        </td>
                      )}
                      {scelta.cagr && (
                        <td className="sb-tono-neutro">
                          {r.cagr == null ? 'n/d' : pct(r.cagr, 1)}
                        </td>
                      )}
                      <td
                        className="sb-tono-neutro"
                        style={{ fontSize: 'var(--sb-t-105)', color: 'var(--sb-testo-tenue)' }}
                      >
                        {periodoDiRiga(r)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </ContenitoreDati>
    </div>
  );
}

/* ==========================================================================
 * SCHEDA (telefono)
 * ======================================================================== */

function SchedaConfigurazione({
  riga: r,
  posizione,
  scelta,
  onApri,
}: {
  riga: BacktestRiga;
  posizione: number;
  scelta: SceltaColonne;
  onApri: (id: number) => void;
}) {
  const periodo = periodoDiRiga(r);

  /* Il pulsante avvolge il contenuto invece di ESSERE la scheda: un `button`
     con dentro griglie e blocchi impagina in modo diverso fra i motori. È lo
     stesso accorgimento di `SchedaRisultato` nei componenti condivisi. */
  return (
    <div className="sb-gr-scheda">
      <button
        type="button"
        onClick={() => onApri(r.id)}
        style={{ all: 'unset', display: 'block', width: '100%', cursor: 'pointer' }}
      >
        <span className="sb-gr-scheda__testata">
          <span className={`sb-gr-rango${posizione <= 3 ? ' sb-gr-rango--testa' : ''}`}>
            #{num(posizione)}
          </span>
          <IconaStrategia nome={r.strategyName} dim={22} />
          <span className="sb-gr-parametri">{parametriLeggibili(r.strategyParams)}</span>
        </span>

        {/* I tre della terna restano tre anche a 390px, e non si spengono. */}
        <TernaMetriche
          densita="riquadri"
          profitto={r.strategyChange}
          drawdown={r.drawdownMaxValue}
          operazioni={r.tradesNumber}
          periodo={periodo}
        />

        <span className="sb-gr-soglie">
          {scelta.tenuta && (
            <Tenuta
              valore={r.walkForwardScore}
              divisione={divisioneDaMetodo(r.walkForwardMetodo)}
              periodo={periodo}
            />
          )}
          {scelta.ribassi && (
            <NeiRibassi valore={r.bearYearsAvg} anniNegativi={r.bearYearsCount} periodo={periodo} />
          )}
          {scelta.intorno && (
            <Intorno
              valore={r.intorno}
              parametroPiuFragile={r.intornoParametro}
              assi={r.intornoAssi}
              periodo={periodo}
            />
          )}
          {scelta.pariRischio && (
            <NumeroConSoglia
              titolo="A pari rischio"
              lettura={leggiAPariRischio(r.strategyChange, r.drawdownMaxValue)}
              periodo={periodo}
            />
          )}
        </span>

        {(scelta.calmar || scelta.sharpe || scelta.cagr) && (
          <span className="sb-gr-coda">
            {scelta.calmar && (
              <span>
                Calmar <b>{r.calmar == null ? 'n/d' : num(r.calmar, 2)}</b>
              </span>
            )}
            {scelta.sharpe && (
              <span>
                Sharpe <b>{r.sharpe == null ? 'n/d' : num(r.sharpe, 2)}</b>
              </span>
            )}
            {scelta.cagr && (
              <span>
                CAGR <b>{r.cagr == null ? 'n/d' : pct(r.cagr, 1)}</b>
              </span>
            )}
          </span>
        )}
      </button>
    </div>
  );
}
